/**
 * Every place the onboarding tour can point at.
 *
 * A view marks an element with `{...tourAnchor('panel.btn.panic')}`; a story
 * step names the same id as its `target`, `also` or `click`. Because the ids
 * are one const array, a typo in either place is a compile error. The one
 * exception is a row inside a card, `panel.row.<subject>`: its subject is a
 * scenario's name for someone, so it is a template type, checked by the
 * anchor tests instead.
 *
 * Ids are `surface.area.thing`: `tour.*` the tour's own cards, `shell.*` the
 * webapp chrome, `chat.*` the Home chat, `panel.*` the Dooglie side panel,
 * `crm.*` the example CRM. Later work packages only add to this list.
 *
 * No app imports: this file moves verbatim to packages/ui.
 */
export const ANCHORS = [
  // tour: the tour's own real-step cards and the stage
  'tour.welcome',
  'tour.card.extension',
  'tour.extension.add',
  'tour.card.pin',
  'tour.card.desktop',
  'tour.desktop.connect',
  'tour.clock',
  'tour.golive',
  'tour.golive.go',

  // shell: webapp navigation (src/shell/navItems.ts SHELL_ANCHORS)
  'shell.nav.home',
  'shell.nav.calls',
  'shell.nav.onboarding',
  'shell.nav.users',
  'shell.nav.billing',
  'shell.nav.value',
  'shell.nav.extension',
  'shell.nav.desktop',

  // chat: the Home discovery chat
  'chat.input',
  'chat.list',
  'chat.attach',

  // panel: the Dooglie side panel
  'panel.tabs',
  'panel.tab.insights',
  'panel.tab.meetings',
  'panel.attach',
  'panel.header.progress',
  'panel.outcome',
  'panel.controls',
  'panel.btn.new',
  'panel.btn.panic',
  'panel.btn.stop',
  'panel.sheet',
  'panel.sheet.pick.hiringManager',
  'panel.sheet.pick.candidate',
  'panel.sheet.options',
  'panel.sheet.option.capture',
  'panel.sheet.option.participant',
  'panel.sim',
  'panel.sim.play',
  'panel.transcript',
  'panel.card.panic',
  'panel.card.review',
  'panel.card.stand',
  'panel.card.job-overview',
  'panel.card.suggested',
  'panel.card.skill-gap',
  'panel.card.person-overview',
  'panel.card.saythis',
  'panel.card.attendees',
  'panel.card.opening',
  'panel.card.background',
  'panel.card.contact-profile',
  'panel.card.client-context',
  'panel.card.market-rate',
  'panel.card.client-context-role',
  'panel.card.project-behind',
  'panel.card.rate-benchmark',
  'panel.card.churn',
  'panel.card.engagement',
  'panel.btn.select',
  'panel.btn.attendees',
  'panel.attendees',
  'panel.ask',

  // crm: the example CRM (every skin renders the same ids)
  'crm.app',
  'crm.nav',
  'crm.nav.contacts',
  'crm.nav.jobs',
  'crm.list',
  'crm.row.hiringManager',
  'crm.row.job',
  'crm.row.pipeline.candidate',
  'crm.pipeline.pick.candidate',
  'crm.pipeline.add',
  'crm.record',
  'crm.record.head',
  'crm.record.filled',
  'crm.tab.details',
  'crm.tab.notes',
  'crm.tab.candidates',
  'crm.note.latest',
] as const;

/** Every fixed anchor, plus a row inside a card by its subject (`@dooglie/ui` `rowAnchor`). */
export type AnchorId = (typeof ANCHORS)[number] | `panel.row.${string}`;

/** The attribute to spread onto an element the tour can light. */
export function tourAnchor(id: AnchorId): { 'data-tour': AnchorId } {
  return { 'data-tour': id };
}

/** CSS selector for every element carrying an anchor id. */
export function anchorSelector(id: AnchorId): string {
  return `[data-tour="${id}"]`;
}

export function isAnchorId(value: string): value is AnchorId {
  return (ANCHORS as readonly string[]).includes(value) || /^panel\.row\.[^.]/.test(value);
}

/** The anchor of a row inside a card: the row's subject, a person's name. */
export function rowAnchorOf(subject: string): AnchorId {
  return `panel.row.${subject}`;
}
